'use strict';
(() => {
  const panels = new WeakMap();

  function initializePanel(dialog) {
    if (panels.has(dialog)) return panels.get(dialog);
    const input = dialog.querySelector('[data-search-input]');
    const clear = dialog.querySelector('[data-search-clear]');
    const results = dialog.querySelector('[data-search-results]');
    const status = dialog.querySelector('[data-search-status]');
    if (!input || !clear || !results || !status) return null;
    let opener = null;
    let timer = null;
    let request = null;
    let sequence = 0;

    function cancelPending() {
      sequence += 1;
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
      request?.abort();
      request = null;
      results.removeAttribute('aria-busy');
    }

    function resetResults(message) {
      results.replaceChildren();
      results.hidden = true;
      status.textContent = message;
    }

    async function fetchSuggestions(query, currentSequence) {
      const controller = new AbortController();
      request = controller;
      const timeout = window.setTimeout(() => controller.abort(), 12000);
      try {
        const url = new URL(dialog.dataset.predictiveUrl, window.location.href);
        if (url.origin !== window.location.origin) throw new Error('Invalid search destination.');
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product,collection');
        url.searchParams.set('resources[limit]', '4');
        url.searchParams.set('resources[limit_scope]', 'all');
        url.searchParams.set('section_id', dialog.dataset.searchSection || 'mh-predictive-search');
        const response = await fetch(url.href, {
          signal: controller.signal,
          credentials: 'same-origin',
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error('Suggestions unavailable.');
        const markup = await response.text();
        if (currentSequence !== sequence || !dialog.open || input.value.trim() !== query) return;
        const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
        const sections = documentFragment.querySelectorAll('[data-predictive-search-results]');
        const section = sections[0];
        if (sections.length !== 1 || section.dataset.searchPerformed !== 'true') throw new Error('Invalid suggestions response.');
        if (section.querySelector('script, style, link, iframe, object, embed, form, input, button, textarea, select, dialog')) throw new Error('Unexpected suggestion controls.');
        for (const element of [section, ...section.querySelectorAll('*')]) {
          if ([...element.attributes].some(attribute => /^on/i.test(attribute.name))) throw new Error('Unexpected suggestion attributes.');
        }
        for (const link of section.querySelectorAll('a')) {
          const destination = new URL(link.getAttribute('href'), window.location.href);
          if (destination.origin !== window.location.origin || !['http:', 'https:'].includes(destination.protocol)) {
            throw new Error('Invalid suggestion destination.');
          }
        }
        for (const image of section.querySelectorAll('img[src]')) {
          if (!['http:', 'https:'].includes(new URL(image.getAttribute('src'), location.href).protocol)) throw new Error('Invalid suggestion image.');
        }
        results.replaceChildren(document.importNode(section, true));
        results.hidden = false;
        const count = section.querySelectorAll('[data-predictive-result]').length;
        status.textContent = count === 0 ? 'No suggestions. Submit your search to see all results.' : `${count} ${count === 1 ? 'suggestion' : 'suggestions'}. Submit your search to see all results.`;
      } catch (error) {
        if (currentSequence !== sequence || !dialog.open) return;
        resetResults('Suggestions are unavailable. Submit your search to see all results.');
      } finally {
        window.clearTimeout(timeout);
        if (currentSequence === sequence) {
          results.removeAttribute('aria-busy');
          if (request === controller) request = null;
        }
      }
    }

    function queueSearch() {
      cancelPending();
      const query = input.value.trim();
      clear.disabled = input.value.length === 0;
      if (!query) {
        resetResults('Search for a piece or collection.');
        return;
      }
      resetResults('Searching…');
      results.setAttribute('aria-busy', 'true');
      const currentSequence = sequence;
      timer = window.setTimeout(() => {
        timer = null;
        if (dialog.open && currentSequence === sequence) fetchSuggestions(query, currentSequence);
      }, 200);
    }

    input.addEventListener('input', queueSearch);
    clear.addEventListener('click', () => {
      input.value = '';
      queueSearch();
      input.focus();
    });
    dialog.querySelector('[data-search-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('keydown', event => { if (event.key === 'Escape') { event.preventDefault(); dialog.close(); } });
    dialog.addEventListener('close', () => {
      cancelPending();
      resetResults('Search for a piece or collection.');
      if (opener) {
        opener.setAttribute('aria-expanded', 'false');
        if (opener.isConnected) opener.focus({ preventScroll: true });
      }
      opener = null;
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    });
    const panel = {
      open(trigger) {
        if (typeof dialog.showModal !== 'function') return false;
        if (!dialog.open) dialog.showModal();
        opener = trigger;
        trigger.setAttribute('aria-expanded', 'true');
        input.focus({ preventScroll: true });
        queueSearch();
        return true;
      },
      cancel: cancelPending
    };
    panels.set(dialog, panel);
    return panel;
  }

  document.addEventListener('click', event => {
    const trigger = event.target.closest('a[data-search-open]');
    if (!trigger || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const dialog = document.getElementById('mh-search');
    if (!(dialog instanceof HTMLDialogElement)) return;
    const panel = initializePanel(dialog);
    try {
      if (panel?.open(trigger)) event.preventDefault();
    } catch {
      // The real anchor still opens the native search page if the dialog fails.
    }
  });
  document.addEventListener('shopify:section:unload', () => {
    const dialog = document.getElementById('mh-search');
    if (!dialog) return;
    panels.get(dialog)?.cancel();
    if (dialog.open) dialog.close();
  });
})();
